import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/Chat';
import MoodIcon from '@mui/icons-material/Mood';
import CreateIcon from '@mui/icons-material/Create';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
//import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import MeditationIcon from '@mui/icons-material/SelfImprovement';
import MusicIcon from '@mui/icons-material/MusicNote';
import BrushIcon from '@mui/icons-material/Brush';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuth } from '../context/AuthContext';
import { apiClient, ENDPOINTS } from '../services/apiConfig';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  DoughnutController
} from 'chart.js';


import AirIcon from '@mui/icons-material/Air';
import EditIcon from '@mui/icons-material/Edit';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
//import { set } from 'mongoose';


// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  DoughnutController
);

// Mock data for mood history
const moodHistory = [
  { date: '2025-06-01', mood: 'Happy', score: 8, factors: ['Exercise', 'Social Interaction', 'Creative Time'] },
  { date: '2025-06-02', mood: 'Calm', score: 7, factors: ['Meditation', 'Good Sleep'] },
  { date: '2025-06-03', mood: 'Anxious', score: 4, factors: ['Work Stress', 'Poor Sleep'] },
  { date: '2025-06-04', mood: 'Motivated', score: 9, factors: ['Exercise', 'Creative Time'] },
  { date: '2025-06-05', mood: 'Tired', score: 5, factors: ['Poor Sleep', 'Work Stress'] },
  { date: '2025-06-06', mood: 'Content', score: 6, factors: ['Social Interaction', 'Creative Time'] },
  { date: '2025-06-07', mood: 'Excited', score: 8, factors: ['Good News', 'Creative Time'] },
];



// Daily craft prompts
const craftPrompts = [
  "Draw your current mood using only 3 colors",
  "Write a haiku about something that made you smile today",
  "Create a collage representing your emotional journey this week",
  "Sketch a place where you feel most at peace",
  "Write a letter to your future self",
  "Draw a symbol that represents your strength",
  "Create a playlist of songs that match your current emotional state",
];


interface MoodEntry {
  date: string;
  primaryEmotion: string;
  subEmotion: string;
  score: number;
  factors?: string[];
  // Add other properties as needed
}

const Dashboard = () => {
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const [greeting, setGreeting] = useState('');
  const [dailyPrompt, setDailyPrompt] = useState('');
  const [moodData, setMoodData] = useState([]);
  const [howToStartOpen, setHowToStartOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastCraft, setLastCraft] = useState(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [latestCrafts, setLatestCrafts] = useState<any[]>([]);
  
  const fetchLastCraft = useCallback(async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.CREATIVE_CRAFTS);
      if (response.data && response.data.length > 0) {
        // Get the most recent craft (assuming they're sorted by date)
        const mostRecentCraft = response.data.sort((a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];
        setLastCraft(mostRecentCraft);
      }
    } catch (err) {
      console.error('Error fetching last craft:', err);
    }
  }, []);
  
  const fetchLatestCrafts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
      const response = await apiClient.get(ENDPOINTS.CREATIVE_CRAFTS, config);
      const sorted = [...response.data].sort(
        (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setLatestCrafts(sorted.slice(0, 3));
    } catch (err) {
      console.error('Error fetching latest crafts:', err);
    }
  }, []);
  

const [moodInsights, setMoodInsights] = useState({
    trend: 'improving', // 'improving', 'declining', or 'stable'
    averageMood: 0,
    mostFrequentMood: '',
    weeklyActivities: [
      { name: 'Mindfulness Technique', icon: <MeditationIcon sx={{ color: '#6C8DFA' }} />, path: '/self-help' },
      { name: 'Journaling', icon: <EditIcon sx={{ color: '#FF9F7E' }} />, path: '/creative-outlet' },
      { name: 'Self-Care', icon: <AirIcon sx={{ color: '#4CAF50' }} />, path: '/self-help' },
    ]
  });
  
  const fetchMoodData = useCallback(async () => {
    try {
      setIsLoading(true);      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setIsLoading(false);
        return;
      }
      
      // Configure headers with token
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      // Fetch mood entries from backend
      //const response = await axios.get('http://localhost:5000/api/moods', config);
      const response = await apiClient.get(ENDPOINTS.MOODS, config);
      
      if (response.data && response.data.length > 0) {
        setMoodData(response.data);
        calculateMoodInsights(response.data);
      } else {
        setMoodData([]);
        setError('No mood entries found');
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching mood data:', err);
      setError('Failed to fetch mood data');
      setIsLoading(false);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  
  // Calculate mood insights based on the fetched data
  const calculateMoodInsights = (data: MoodEntry[]) => {
  if (!data || data.length === 0) return;
  
  // Calculate average mood
  const recentMoods = data.slice(0, 5);
  const averageMood = recentMoods.reduce((sum: number, entry: MoodEntry) => sum + entry.score, 0) / recentMoods.length;
  
  // Determine mood trend
  let trend = 'stable';
  if (data.length > 5) {
    const olderMoods = data.slice(5, 10);
    const olderAverage = olderMoods.reduce((sum: number, entry: MoodEntry) => sum + entry.score, 0) / olderMoods.length || 0;
    
    if (averageMood > olderAverage + 0.5) trend = 'improving';
    else if (averageMood < olderAverage - 0.5) trend = 'declining';
  }
  
  // Find most frequent mood
  const moodCounts: Record<string, number> = {};
  data.forEach((entry: MoodEntry) => {
    if (!moodCounts[entry.primaryEmotion]) {
      moodCounts[entry.primaryEmotion] = 0;
    }
    moodCounts[entry.primaryEmotion]++;
  });
  
  const mostFrequentMood = Object.keys(moodCounts).reduce((a, b) => 
    moodCounts[a] > moodCounts[b] ? a : b, '');
  
  setMoodInsights({
    ...moodInsights,
    trend,
    averageMood,
    mostFrequentMood
  });
};
  
  // Calculate mood distribution data
  const calculateMoodDistribution = () => {
    if (!moodData || moodData.length === 0) {
      return {
        labels: [],
        data: [],
        backgroundColor: [],
        totalEntries: 0,
        mostFrequentMood: ''
      };
    }
    
    const moodCounts = {} as Record<string, number>;
    const moodColors = {
      'Joy': '#4CAF50',      // Bright green
      'Trust': '#2196F3',    // Blue
      'Fear': '#FFC107',     // Amber
      'Surprise': '#FF9800',  // Orange
      'Sadness': '#607D8B',   // Blue grey
      'Disgust': '#F44336',   // Red
      'Anger': '#FF5722',     // Deep orange
      'Anticipation': '#9C27B0', // Purple
      'Content': '#00BCD4',   // Cyan
      'Excited': '#8BC34A',   // Light green
      'Grateful': '#CDDC39',  // Lime
      'Calm': '#3F51B5',      // Indigo
      'Anxious': '#FFEB3B',   // Yellow
      'Frustrated': '#E91E63', // Pink
      'Overwhelmed': '#9E9E9E', // Grey
      'Hopeful': '#009688',    // Teal
      'Happy': '#4CAF50',     // Bright green (same as Joy)
      'Tired': '#9E9E9E',     // Grey
      'Motivated': '#8BC34A',  // Light green
      'Sad': '#607D8B'        // Blue grey (same as Sadness)
    };

    // Log the mood data to see what emotions are actually present
    console.log('Mood data for distribution:', moodData);

    moodData.forEach(entry => {
      const emotion = (entry as MoodEntry).primaryEmotion;
      if (!moodCounts[emotion]) {
        moodCounts[emotion] = 0;
      }
      moodCounts[emotion]++;
    });

    const labels = Object.keys(moodCounts);
    const data = Object.values(moodCounts);
    
    // Create the background colors array and log it for debugging
    const backgroundColor = labels.map(mood => {
      const color = (mood in moodColors) ? moodColors[mood as keyof typeof moodColors] : '#9E9E9E';
      console.log(`Mood: ${mood}, Color: ${color}`);
      return color;
    });
    
    console.log('Final backgroundColor array:', backgroundColor);
    
    const totalEntries = data.reduce((sum, count) => sum + count, 0);
    const mostFrequentMood = labels[data.indexOf(Math.max(...data))];

    return {
      labels,
      data,
      backgroundColor,
      totalEntries,
      mostFrequentMood
    };
  };
  
  // Get top emotions
 const getTopEmotions = () => {
    if (!moodData || moodData.length === 0) return [];
    
    const emotionCounts = {} as Record<string, number>;
    
    moodData.forEach(entry => {
      const emotion = (entry as MoodEntry).primaryEmotion;
      // Skip empty emotions
      if (!emotion) return;
      
      if (!emotionCounts[emotion]) {
        emotionCounts[emotion] = 0;
      }
      emotionCounts[emotion]++;
    });
    
    return Object.keys(emotionCounts)
      .filter(emotion => emotion) // Filter out any undefined or empty emotions
      .map(emotion => ({ emotion, count: emotionCounts[emotion] }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  };
  
  // Get top triggers
  const getTopTriggers = () => {
  if (!moodData || moodData.length === 0) return [];
  
  const triggerCounts: Record<string, number> = {};
  const positiveFactors = ['Exercise', 'Social Interaction', 'Creative Activities', 'Good Sleep', 'Meditation'];
  
  moodData.forEach((entry: MoodEntry) => {
    if (entry.factors && entry.factors.length > 0) {
      entry.factors.forEach((factor: string) => {
        if (!triggerCounts[factor]) {
          triggerCounts[factor] = 0;
        }
        triggerCounts[factor]++;
      });
    }
  });
  
  return Object.keys(triggerCounts)
    .map(trigger => ({
      trigger,
      count: triggerCounts[trigger],
      positive: positiveFactors.includes(trigger)
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);
};
  
  // Get recommended activities based on mood
  const getRecommendedActivities = () => {
    const averageMood = moodInsights.averageMood;
    
    if (averageMood >= 7) {
      return [        
        { title: 'Creative Expression', icon: <CreateIcon />, color: '#6C8DFA', path: '/creative-outlet'},
        { title: 'Mood Reflection', icon: <MoodIcon />, color: '#6C8DFA', path: '/mood-tracker' },
      ];
    } else if (averageMood >= 5) {
      return [        
        { title: 'Mood Boosting Music', icon: <MusicIcon />, color: '#8C7CFA', path: '/creative-outlet' },
        { title: 'Chat with Mood Friend', icon: <ChatIcon />, color: '#8C7CFA', path: '/chat' },
      ];
    } else {
      return [        
        { title: 'Anxiety Relief Help', icon: <SchoolIcon />, color: '#FF9F7E', path: '/self-help/anxiety' },
        { title: 'Chat with Mood Friend', icon: <ChatIcon />, color: '#FF9F7E', path: '/chat' },
      ];
    }
  };
  
  useEffect(() => {
    if (!loading && !currentUser) {
      navigate('/login');
    }
    // Set greeting based on time of day
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 18) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
    
    // Set random daily prompt
    const randomIndex = Math.floor(Math.random() * craftPrompts.length);
    setDailyPrompt(craftPrompts[randomIndex]);
    
    // Fetch mood data
    fetchMoodData();
    
    // Fetch last creative craft
    fetchLastCraft();
    
    // Fetch latest crafts
    fetchLatestCrafts();
  }, [currentUser, loading, navigate, fetchMoodData, fetchLastCraft, fetchLatestCrafts]);

    // Inside the Dashboard component, update the render section
  
  // Get the calculated data
  const moodDistribution = calculateMoodDistribution();
  const topEmotionsData = getTopEmotions();
  const topTriggersData = getTopTriggers();
  const recommendedActivities = getRecommendedActivities();
  
  // Create chart data
  const doughnutData = {
    labels: moodDistribution.labels,
    datasets: [
      {
        data: moodDistribution.data,
        backgroundColor: [
          '#4CAF50',  // Green
          '#2196F3',  // Blue
          '#FFC107',  // Amber
          '#FF9800',  // Orange
          '#607D8B',  // Blue Grey
          '#F44336',  // Red
          '#FF5722',  // Deep Orange
          '#9C27B0',  // Purple
          '#00BCD4',  // Cyan
          '#8BC34A',  // Light Green
          '#CDDC39',  // Lime
          '#3F51B5',  // Indigo
          '#FFEB3B',  // Yellow
          '#E91E63',  // Pink
          '#9E9E9E',  // Grey
          '#009688',  // Teal
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 10,
      },
    ],
  };

  // Add the mood insights useEffect inside the component
  useEffect(() => {
    // Calculate insights based on mood history
    const frustrationEntries = moodHistory.filter(entry => 
      entry.mood === 'Frustrated' || entry.mood === 'Angry' || entry.mood === 'Irritated'
    );
          
    
    setMoodInsights({
      trend: frustrationEntries.length > 0 ? 'declining' : 'improving',
      weeklyActivities: [
        { 
          name: 'Mindfulness Meditation',
          icon: <SelfImprovementIcon sx={{ color: '#6C8DFA' }} />,
          path: '/self-help'
        },
        {
          name: 'Journaling',
          icon: <EditIcon sx={{ color: '#FF9F7E' }} />,
          path: '/creative-outlet'
        },
        {
          name: 'Deep Breathing',
          icon: <AirIcon sx={{ color: '#4CAF50' }} />,
          path: '/self-help'
        }
      ],
// Remove duplicate trend property since it's already defined above
      averageMood: 7.2,
      mostFrequentMood: 'content',
      // Remove suggestedActivity since it's not defined in the state type
      
    });
  }, []);  // Only run once on component mount


  // Prepare chart data with enhanced styling
  const chartData = {
    labels: moodData.length > 0 ? [...moodData].sort((a: MoodEntry, b: MoodEntry) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    ).map((entry: MoodEntry) => entry.date) : [],
    datasets: [
      {
        label: 'Mood Score',
        data: moodData.length > 0 ? [...moodData].sort((a: MoodEntry, b: MoodEntry) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        ).map((entry: MoodEntry) => entry.score) : [],
        fill: true,
        backgroundColor: 'rgba(108, 141, 250, 0.2)',
        borderColor: '#6C8DFA',
        tension: 0.4,
        pointBackgroundColor: moodData.length > 0 ? [...moodData].sort((a: MoodEntry, b: MoodEntry) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        ).map((entry: MoodEntry) => {
          // Color points based on mood score
          if (entry.score >= 7) return '#4CAF50'; // Good mood - green
          if (entry.score >= 5) return '#FFC107'; // Neutral mood - amber
          return '#F44336'; // Low mood - red
        }) : [],
        pointBorderColor: '#fff',
        pointRadius: 6,
        pointHoverRadius: 8,
        borderWidth: 3,
      },
    ],
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <React.Fragment> 
      <Container maxWidth="lg">          
        <Box sx={{           
          p: 4,
          mb: 2,
          color: 'white'
        }}>          
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, color: '#4A306D', fontFamily: '"Lato", sans-serif'  }}>
                  {greeting}, {currentUser?.name}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#4A306D', opacity: 0.8, fontFamily: '"Lato", sans-serif' }}>
                  Welcome to MoodCraft, your emotional wellness companion. 
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<HelpOutlineIcon />}
                onClick={() => setHowToStartOpen(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'white',
                  background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
                  boxShadow: '0 10px 26px rgba(108, 141, 250, 0.35)',
                  '@keyframes howToStartPulse': {
                    '0%': { boxShadow: '0 10px 26px rgba(108, 141, 250, 0.35)' },
                    '50%': { boxShadow: '0 14px 34px rgba(255, 159, 126, 0.45)' },
                    '100%': { boxShadow: '0 10px 26px rgba(108, 141, 250, 0.35)' },
                  },
                  animation: 'howToStartPulse 2.6s ease-in-out infinite',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5b7ef8 0%, #ff8e69 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 16px 38px rgba(108, 141, 250, 0.45)',
                  },
                }}
              >
                How to Start
              </Button>
            </Box>
        </Box>

        <Dialog
          open={howToStartOpen}
          onClose={() => setHowToStartOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ fontFamily: '"Lato", sans-serif' }}>How to Start</DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ mb: 2, fontFamily: '"Lato", sans-serif' }}>
              MoodCraft helps you track your mood, to know your mood trend, reflect through journaling, by writing, drawing, talking (audio), or showing (video), you also can use AI-powered chat to have coversations or seek support.  Self-help provides some resources to you as references.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontFamily: '"Lato", sans-serif' }}>
              Main menu
            </Typography>
            <List dense>
              <ListItem alignItems="flex-start">
                <ListItemIcon><DashboardIcon /></ListItemIcon>
                <ListItemText
                  primary="Dashboard"
                  secondary="Your overview: quick stats, mood insights, and a daily creative prompt to get started."
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon><ChatIcon /></ListItemIcon>
                <ListItemText
                  primary="Chat"
                  secondary="Talk with the AI companion for guidance, coping ideas, and gentle check-ins."
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon><MoodIcon /></ListItemIcon>
                <ListItemText
                  primary="Mood Tracker"
                  secondary="Log your mood and factors, then view trends over time."
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon><CreateIcon /></ListItemIcon>
                <ListItemText
                  primary="Your Craft"
                  secondary="Create and save journaling/creative entries to express and reflect on your emotions."
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon><SchoolIcon /></ListItemIcon>
                <ListItemText
                  primary="Self-Help"
                  secondary="Browse guided modules and practical techniques for stress, anxiety, motivation, and wellbeing."
                />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1, fontFamily: '"Lato", sans-serif' }}>
              Tips
            </Typography>
            <List dense>
              <ListItem alignItems="flex-start">
                <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                <ListItemText
                  primary="Build a simple routine"
                  secondary="Try logging your mood once per day. After a week you’ll start seeing patterns and helpful insights."
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon><MeditationIcon /></ListItemIcon>
                <ListItemText
                  primary="Use short practices"
                  secondary="When you feel overwhelmed, try a quick grounding or breathing exercise in Self-Help."
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon><MusicIcon /></ListItemIcon>
                <ListItemText
                  primary="Express your mood creatively"
                  secondary="Use the daily prompt in Your Craft to turn feelings into something tangible."
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon><AccountCircleIcon /></ListItemIcon>
                <ListItemText
                  primary="Update your Profile"
                  secondary="Adjust your preferences and review your account information anytime."
                />
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHowToStartOpen(false)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

          {/* Craft Prompt of the Day */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0,
              mb: 3,
              borderRadius: 2,
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
              color: 'white',
              position: 'relative',
            }}
          >
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              opacity: 0.1,
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.343 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="1" fill-rule="evenodd"/%3E%3C/svg%3E")',
            }} />
            <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LightbulbIcon sx={{ mr: 1, fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, fontFamily: '"Lato", sans-serif' }}>
                    Craft of the Day
                  </Typography>
                </Box>
                
                <Button 
                  variant="contained" 
                  startIcon={<BrushIcon />}
                  onClick={() => navigate('/creative-outlet')}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)', 
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                    },
                    backdropFilter: 'blur(5px)',
                    borderRadius: 2,
                    boxShadow: 'none',
                    ml: 2, // Add margin to the left
                  }}
                >
                  Start Creating
                </Button>
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 300, mb: 3, lineHeight: 1.4, fontFamily: '"Lato", sans-serif' }}>
                "{dailyPrompt}"
              </Typography>
              
            </Box>
          </Paper>
          
          <Grid container spacing={3}>
            {/* Quick Stats */}
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 400, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
                  Top Emotions
                </Typography>
                <List dense>
                  {topEmotionsData.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.25 }}>
                      <ListItemIcon>
                        <Box 
                          sx={{ 
                            p: 1, 
                            borderRadius: '50%', 
                            backgroundColor: `rgba(108, 141, 250, ${0.2 - (index * 0.05)})`,
                            fontFamily: '"Quicksand", sans-serif'
                          }}
                        >
                          <MoodIcon 
                            sx={{ color: '#6C8DFA' }}
                            fontSize="small"
                          />
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.emotion} 
                        primaryTypographyProps={{ fontWeight: 400 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                        {item.count} times
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 400, color: '#4A306D', mt: 1, fontFamily: '"Lato", sans-serif' }}>
                  Top Triggers
                </Typography>
                <List dense>
                  {topTriggersData.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.25, fontFamily: '"Quicksand", sans-serif' }}>
                      <ListItemIcon>
                        <Box 
                          sx={{ 
                            p: 1, 
                            borderRadius: '50%', 
                            backgroundColor: item.positive ? 'rgba(108, 141, 250, 0.2)' : 'rgba(255, 159, 126, 0.2)',
                          }}
                        >
                          <MoodIcon 
                            sx={{ color: item.positive ? '#6C8DFA' : '#FF9F7E' }}
                            fontSize="small"
                          />
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.trigger} 
                        primaryTypographyProps={{ fontWeight: 400 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                        {item.count} times
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            
            {/* Widgets - Chat with AI */}
            <Grid item xs={12} md={4}>
              <Card 
                elevation={0} 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  border: '2px solid #6C8DFA',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'visible',
                  boxShadow: '0 4px 20px rgba(108, 141, 250, 0.15)'
                }}
              >
                <Box 
                  sx={{
                    position: 'absolute',
                    top: { xs: -10, md: -12 },
                    right: { xs: 12, md: 20 },
                    bgcolor: '#FF9F7E',
                    color: 'white',
                    py: 0.5,
                    px: 1.5,
                    borderRadius: 10,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    zIndex: 1,
                    boxShadow: '0 2px 8px rgba(255, 159, 126, 0.3)'
                  }}
                >
                  New!
                </Box>

                <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: '50%', 
                      bgcolor: 'rgba(108, 141, 250, 0.1)',
                      color: '#6C8DFA',
                      mr: 2
                    }}>
                      <ChatIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 500, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
                      Chat with Mood Friend
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: '"Quicksand", sans-serif' }}>
                    Your Mood Friend is here to listen and provide support whenever you need it.
                  </Typography>
                  
                  <Box sx={{ 
                    p: { xs: 2, md: 3 }, 
                    bgcolor: 'rgba(108, 141, 250, 0.08)', 
                    borderRadius: 3,
                    border: '2px solid rgba(108, 141, 250, 0.4)',
                    boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.05)',
                    mb: 3,
                    flexGrow: 1,
                  }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontStyle: 'italic', 
                        color: '#4A306D', 
                        lineHeight: 1.6, 
                        fontFamily: '"Quicksand", sans-serif'
                      }}
                    >
                      "How are you feeling today? I’m here to chat whenever you’re ready."
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: { xs: 2, md: 3 }, pt: 0 }}>
                  <Button 
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/chat')}
                    sx={{ 
                      bgcolor: '#6C8DFA',
                      '&:hover': { bgcolor: '#5A7DE0' },
                      borderRadius: 2,
                      py: { xs: 1.1, md: 1.4 },
                      boxShadow: 'none',
                    }}
                  >
                    Start Chatting Now
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            
            {/* Inspiring Crafts */}
            <Grid item xs={12} md={4}>
              <Card 
                elevation={0} 
                sx={{ 
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    pb: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: '50%', 
                      bgcolor: 'rgba(255, 159, 126, 0.1)',
                      color: '#FF9F7E',
                      mr: 2
                    }}>
                      <CreateIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 400, color: '#4A306D' }}>
                      Inspiring Crafts
                    </Typography>
                  </Box>

                  {latestCrafts.length > 0 ? (
                    <Grid container spacing={1}>
                      {latestCrafts.map((craft, idx) => (
                        <Grid item xs={12} key={idx}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            p: 1.25, 
                            borderRadius: 1, 
                            border: '1px solid', 
                            borderColor: 'divider' 
                          }}>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize', color: 'text.secondary' }}>
                              {(craft as any).type
}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {(craft as any).title}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Start creating to see inspiring crafts here
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button 
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/creative-outlet')}
                    sx={{ 
                      bgcolor: '#FF9F7E',
                      '&:hover': {
                        bgcolor: '#F08E6E',
                      },
                    }}
                  >
                    Continue Creating
                  </Button>
                </CardActions>
              </Card>
            </Grid>
    


{/* Weekly Mood Summary */}
    <Grid item xs={12}>
      <Paper 
         elevation={0} 
          sx={{ 
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >  
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#4A306D' , fontFamily: '"Lato", sans-serif' }}>
        Weekly Mood Summary
      </Typography>
      
      <Grid container spacing={{ xs: 2, md: 6 }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 1 }}>
            <Box sx={{ ml: { xs: 1, md: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, fontFamily: '"Lato", sans-serif' }}>
                This Week's Insights
              </Typography>              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5, lineHeight: 1.4, fontFamily: '"Quicksand", sans-serif' }}>
                  <TrendingUpIcon 
                    sx={{ 
                      mr: 1, 
                      color: moodInsights.trend === 'improving' ? '#4CAF50' : moodInsights.trend === 'stable' ? '#FFC107' : '#F44336',
                      fontSize: '1rem'
                    }} 
                  />
                  Mood Trend: 
                  <Box
                    component="span"
                    sx={{                      
                      ml: 0.5,
                      color:
                        moodInsights.trend === 'improving'
                          ? '#4CAF50'
                          : moodInsights.trend === 'declining'
                          ? '#F44336'
                          : 'text.primary',
                    }}
                  >
                    {moodInsights.trend === 'improving'
                      ? 'Improving'
                      : moodInsights.trend === 'stable'
                      ? 'Stable'
                      : 'Declining'}
                  </Box>
                </Typography>                
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5, lineHeight: 1.4 , fontFamily: '"Quicksand", sans-serif'}}>
                  <MoodIcon sx={{ mr: 1, color: '#6C8DFA', fontSize: '1rem' }} />
                  Average Mood: <Box component="span" sx={{  ml: 0.5 }}>{moodInsights.averageMood.toFixed(1)}/10</Box>
                </Typography>
                {/* Most Frequent line already removed */}
              </Box>
            </Box>
            
            <Box sx={{ ml: { xs: 1, md: 3 } }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: '"Lato", sans-serif', mb: 0.5 }}>
                Suggested Activities：
              </Typography>
              <List dense disablePadding>
                {recommendedActivities.map((activity, index) => (
                  <ListItem 
                    key={index} 
                    disablePadding 
                    sx={{ py: 0.5 }}
                    button
                    onClick={() => navigate(activity.path)}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box sx={{ color: activity.color }}>
                        {activity.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText 
                      primary={activity.title} 
                      primaryTypographyProps={{ variant: 'body2', fontFamily: '"Quicksand", sans-serif' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ height: { xs: 200, md: 250 }, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, fontFamily: '"Lato", sans-serif' }}>
              Mood Distribution
            </Typography>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Doughnut 
                data={doughnutData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '70%',
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        boxWidth: 12,
                        padding: 15,
                        font: {
                          size: 11
                        }
                      }
                    },
                    tooltip: {
                      backgroundColor: 'rgba(74, 48, 109, 0.8)',
                      titleFont: {
                        size: 13
                      },
                      bodyFont: {
                        size: 12
                      },
                      padding: 10,
                      cornerRadius: 6
                    }
                  },
                  animation: {
                    animateScale: true,
                    animateRotate: true
                  }
                }}
              />
            </Box>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1, fontFamily: '"Quicksand", sans-serif' }}>
              Total entries: {moodDistribution.totalEntries}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Box sx={{ height: 250, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, fontFamily: '"Lato", sans-serif' }}>
                Mood Trends
              </Typography>              
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Line data={chartData} />
            </Box>
            <Button 
                variant="outlined" 
                size="small"
                onClick={() => navigate('/mood-tracker')}
                sx={{ 
                  borderColor: '#6C8DFA', 
                  color: '#6C8DFA',
                  fontSize: '0.75rem',
                  py: 0.5,
                  '&:hover': {
                    borderColor: '#5A7DE0',
                    bgcolor: 'rgba(108, 141, 250, 0.05)'
                  }
                }}
              >
                View Analysis Details
              </Button>
          </Box>
        </Grid>
      </Grid>
      </Paper>
    </Grid>
  
    
            {/* Self-Help Articles */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 400, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
                    Self-Help Articles
                  </Typography>
                  <Button 
                    variant="text" 
                    onClick={() => navigate('/self-help')}
                    sx={{ color: '#6C8DFA', fontFamily: '"Quicksand", sans-serif' }}
                  >
                    View All
                  </Button>
                </Box>
                
                <Grid container spacing={2}>
                  {['Mental Health', 'Personal Growth', 'Emotional Resilience'].map((category) => (
                    <Grid item xs={12} md={4} key={category}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#6C8DFA',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                          }
                        }}
                        onClick={() => navigate(`/self-help?category=${encodeURIComponent(category)}`)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 , fontFamily: '"Quicksand", sans-serif' }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: 
                                  category === 'Mental Health' ? 'rgba(108, 141, 250, 0.1)' :
                                  category === 'Personal Growth' ? 'rgba(143, 222, 180, 0.1)' :
                                  'rgba(255, 159, 126, 0.1)',
                                color:
                                  category === 'Mental Health' ? '#6C8DFA' :
                                  category === 'Personal Growth' ? '#8FDEB4' :
                                  '#FF9F7E',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2
                              }}
                            >
                              {/* icon placeholder */}
                              <span style={{ fontWeight: 500, fontFamily: '"Quicksand", sans-serif' }}>★</span>
                            </Box>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {category}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Explore articles and exercises
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                        <CardActions>
                          <Button 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/self-help?category=${encodeURIComponent(category)}`);
                            }} 
                            sx={{ 
                              color: '#6C8DFA',
                              ml: 1,
                              mb: 1,
                              fontFamily: '"Quicksand", sans-serif'
                            }}
                          >
                            View {category}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
      </Container>
    </React.Fragment>  
   
  );
};
export default Dashboard;
               
